import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { fetchCrmUsers, type CrmUserFilters, type CrmUserListItem } from "@/services/adminCrm";
import { UserListFilters } from "./UserListFilters";

const PAGE_SIZE = 50;

type SortColumn = "name" | "messageCount" | "createdAt" | "lastActiveAt";

// ── Access dot ──────────────────────────────────────────────────────────────

function AccessDot({ hasAccess }: { hasAccess: boolean }) {
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full shrink-0 ${hasAccess ? "bg-green-500" : "bg-red-500"}`}
      title={hasAccess ? "Has access" : "No access"}
    />
  );
}

// ── Status badge (Access Type) ──────────────────────────────────────────────

function StatusBadge({ state }: { state: string }) {
  const variants: Record<string, { className: string; label: string }> = {
    active: { className: "bg-green-100 text-green-800 border-green-200", label: "Active" },
    comp: { className: "bg-blue-100 text-blue-800 border-blue-200", label: "Comp" },
    b2b: { className: "bg-green-100 text-green-800 border-green-200", label: "B2B" },
    canceling: { className: "bg-amber-100 text-amber-800 border-amber-200", label: "Canceling" },
    expired: { className: "bg-red-100 text-red-800 border-red-200", label: "Expired" },
    canceled: { className: "bg-red-100 text-red-800 border-red-200", label: "Canceled" },
    past_due: { className: "bg-red-100 text-red-800 border-red-200", label: "Past Due" },
    none: { className: "bg-gray-100 text-gray-600 border-gray-200", label: "None" },
  };
  const v = variants[state] || variants.none;
  return (
    <Badge variant="outline" className={`${v.className} text-xs`}>
      {v.label}
    </Badge>
  );
}

// ── Activation badge ────────────────────────────────────────────────────────

function ActivationBadge({ stage }: { stage: string }) {
  const variants: Record<string, { className: string; label: string }> = {
    activated: { className: "border-green-300 text-green-700", label: "Activated" },
    invited: { className: "border-blue-300 text-blue-700", label: "Invited" },
    lapsed: { className: "border-amber-300 text-amber-700", label: "Lapsed" },
  };
  const v = variants[stage] || variants.lapsed;
  return (
    <Badge variant="outline" className={`${v.className} text-xs`}>
      {v.label}
    </Badge>
  );
}

// ── Avatar ──────────────────────────────────────────────────────────────────

function UserAvatar({ user }: { user: CrmUserListItem }) {
  const initials =
    [user.firstName?.[0], user.lastName?.[0]].filter(Boolean).join("").toUpperCase() ||
    user.email[0].toUpperCase();

  return (
    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground shrink-0">
      {initials}
    </div>
  );
}

// ── Relative time helper ────────────────────────────────────────────────────

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return "Never";
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
  } catch {
    return "-";
  }
}

// ── Main component ──────────────────────────────────────────────────────────

export function UserList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedCustomerId = searchParams.get("customer") || "";

  const hideTest = searchParams.get("hideTest") === "true";

  // Filter state
  const [filters, setFilters] = useState<CrmUserFilters>({});
  const [debouncedFilters, setDebouncedFilters] = useState<CrmUserFilters>({});

  // Sort state
  const [sortBy, setSortBy] = useState<SortColumn>("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // Pagination
  const [skip, setSkip] = useState(0);

  // Debounce filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setSkip(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters]);

  // Build query params
  const queryFilters: CrmUserFilters = useMemo(() => {
    const params: CrmUserFilters = {
      ...debouncedFilters,
      sortBy,
      sortDir,
      skip,
      limit: PAGE_SIZE,
    };
    if (selectedCustomerId) {
      params.customerId = selectedCustomerId;
    }
    if (hideTest) {
      params.hideTest = "true";
    }
    return params;
  }, [debouncedFilters, sortBy, sortDir, skip, selectedCustomerId, hideTest]);

  const { data, isLoading } = useQuery({
    queryKey: ["crm-users", queryFilters],
    queryFn: () => fetchCrmUsers(queryFilters),
    staleTime: 15_000,
  });

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Sort handlers
  function handleSort(column: SortColumn) {
    if (sortBy === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
    setSkip(0);
  }

  function SortIcon({ column }: { column: SortColumn }) {
    if (sortBy !== column) {
      return <ChevronsUpDown className="ml-1 h-4 w-4 inline opacity-50" />;
    }
    return sortDir === "asc" ? (
      <ChevronUp className="ml-1 h-4 w-4 inline" />
    ) : (
      <ChevronDown className="ml-1 h-4 w-4 inline" />
    );
  }

  function SortableHeader({ column, children }: { column: SortColumn; children: React.ReactNode }) {
    return (
      <TableHead
        className="cursor-pointer hover:bg-muted/50 select-none"
        onClick={() => handleSort(column)}
      >
        {children}
        <SortIcon column={column} />
      </TableHead>
    );
  }

  // Filter change handler
  function handleFilterChange(newFilters: CrmUserFilters) {
    setFilters(newFilters);
  }

  // Direct user ID jump
  const userIdInputRef = useRef<HTMLInputElement>(null);
  function handleUserIdJump() {
    const val = userIdInputRef.current?.value.trim() || "";
    if (/^[a-f\d]{24}$/i.test(val)) {
      navigate(`/recruit-admin/crm/user/${val}`);
    }
  }

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">Users</h1>
          {!isLoading && (
            <Badge variant="secondary" className="text-sm">
              {total.toLocaleString()}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Input
            ref={userIdInputRef}
            placeholder="Jump to user ID…"
            className="w-56 h-8 text-sm font-mono"
            onKeyDown={e => e.key === "Enter" && handleUserIdJump()}
          />
          <Button size="sm" variant="outline" onClick={handleUserIdJump}>
            Go
          </Button>
        </div>
      </div>

      {/* Filters */}
      <UserListFilters filters={filters} onFilterChange={handleFilterChange} />

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader column="name">User</SortableHeader>
              {!selectedCustomerId && <TableHead>Customer</TableHead>}
              <TableHead>Type</TableHead>
              <TableHead>Access Type</TableHead>
              <TableHead>Activation</TableHead>
              <SortableHeader column="messageCount">Msgs</SortableHeader>
              <SortableHeader column="createdAt">Created</SortableHeader>
              <SortableHeader column="lastActiveAt">Last Active</SortableHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Skeleton loading rows
              Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-1.5">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-44" />
                      </div>
                    </div>
                  </TableCell>
                  {!selectedCustomerId && (
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                  )}
                  <TableCell>
                    <Skeleton className="h-5 w-14" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-8" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                </TableRow>
              ))
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={selectedCustomerId ? 7 : 8}
                  className="h-24 text-center text-muted-foreground"
                >
                  No users found.
                </TableCell>
              </TableRow>
            ) : (
              users.map(user => (
                <TableRow
                  key={user.userId}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/recruit-admin/crm/user/${user.userId}`)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <UserAvatar user={user} />
                      <div className="min-w-0">
                        <div className="font-medium truncate">{user.displayName || user.email}</div>
                        {user.displayName && (
                          <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  {!selectedCustomerId && (
                    <TableCell className="text-sm text-muted-foreground">
                      {user.customer.name || "-"}
                    </TableCell>
                  )}
                  <TableCell>
                    <Badge variant="outline" className="text-xs capitalize">
                      {user.userType}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <AccessDot hasAccess={user.hasAccess} />
                      <StatusBadge state={user.subscriptionState} />
                    </div>
                  </TableCell>
                  <TableCell>
                    <ActivationBadge stage={user.activationStage} />
                  </TableCell>
                  <TableCell className="tabular-nums">{user.activity.messageCount}</TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {relativeTime(user.createdAt)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {relativeTime(user.lastActiveAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {skip + 1}-{Math.min(skip + PAGE_SIZE, total)} of {total.toLocaleString()}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={skip + PAGE_SIZE >= total}
              onClick={() => setSkip(skip + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
